import { CreateDashboardDefinitionBase } from '../../_shared/dashboard-definition.model';
import { defineDashboard, resolveProduct } from '../../_shared/dashboard-axes';
import { createTimeFilters, resolveTimeFilter } from '../../_shared/time-filters';
import {
  RankDashboardDataTransformer,
  SankeyNodeNameMappingDashboardDataTransformer,
  SourceIdToEntityIdDashboardDataTransformer,
  SumSubMarketNameDataTransformer,
  UndefinedStateToSumGermanyDashboardDataTransformer,
} from '../../../data_transformers/dashboard_data_transformer';
import {
  DashboardMethodologyFromRouteSingleSelectFilter,
  DashboardStateSingleSelectFilter,
  DashboardSubMarketSingleSelectFilter,
  DashboardSubMarketsMultiSelectFilter,
  DashboardWzClassSingleSelectFilter,
  DashboardWzLayerVisibilitySingleSelectFilter,
} from '../../../filters/dashboard_filter_implementations';
import { createIdNameTransformers } from '../../../dashboard-config/granularities/granularity-profile';
import { createKpiFilters } from '../../../dashboard-config/kpis/kpi-filters';
import { KpiColumnDataTransformer, KpiLabelDataTransformer } from '../../../dashboard-config/kpis/kpi-data-transformers';
import { selectedKpi } from '../../../dashboard-config/kpis/kpi-options';
import {
  DossierMetricProfile,
  resolveDossierHeadingLabel,
  resolveDossierHeadingLabels,
} from './dossier-metric.profile';
import { DossierShell, dossierShellProfiles, DossierValueColumn } from './dossier-shell.profile';
import { DossierStatesDefinition } from './dossier-states.definition';

export type { DossierShell };

/** A dossier variant's metric profile: the shared dossier metric, the view and the variant's exceptions. */
export type DossierShellMetricProfile<TView> = DossierMetricProfile & {
  view: TView;
  /** Replaces the shell's value columns (info: count ↔ revenue cross-columns). */
  valueColumns?: DossierValueColumn[];
  /** Ranks before `rank` (Branchenstruktur: one per KPI of count, revenue, GVA). */
  extraRanks?: { rankColumn: string; valueColumn: string }[];
};

export type CreateDossierStatesDefinitionInput<TView> = CreateDashboardDefinitionBase<
  DossierShell,
  DossierShellMetricProfile<TView>
>;

export function createDashboardDossierStatesDefinition<TView>(
  input: CreateDossierStatesDefinitionInput<TView>,
): DossierStatesDefinition<TView> {
  const { axes, shell, metric } = resolveProduct(input, dossierShellProfiles);
  const context = axes.kpiContext;
  // Dossier datasets are yearly, also for Employed; a range starts with every year.
  const time = resolveTimeFilter(axes, shell.time, { yearly: true, defaultYearRange: 'all' });
  const valueColumns = metric.valueColumns ?? shell.valueColumns ?? [];

  return defineDashboard<DossierStatesDefinition<TView>>(axes, shell, {
    heading: (filterValues, product) => {
      // Per-KPI labels are keyed by the KPI label (GVA: Produktionswert).
      const headingLabels = resolveDossierHeadingLabels(metric, selectedKpi(filterValues)?.label);
      const headingLabel = resolveDossierHeadingLabel(headingLabels, shell.headingLabelUsage);
      return shell.createHeading(headingLabel.toLocaleUpperCase('de-DE'))(filterValues, product);
    },
    headingLabels: metric.headingLabels,
    headingLabelsByType: metric.headingLabelsByType,

    // Order: time, state, sub-market, WZ class, KPI, methodology, WZ layer.
    filters: (deps) => [
      ...createTimeFilters(deps, time),
      new DashboardStateSingleSelectFilter(deps.localDatasetsService, { addSumGermany: false }),
      ...(shell.subMarket === 'single'
        ? [
            new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {
              excluded: deps.sumSubMarketIds,
              defaultValue: deps.localDatasetsService.getSumSubMarketIdByMethodologyId(
                +deps.activatedRoute.snapshot.data['defaultMethodology'],
              ),
            }),
          ]
        : []),
      ...(shell.subMarket === 'multi'
        ? [
            new DashboardSubMarketsMultiSelectFilter(deps.localDatasetsService, {
              allInitiallySelectedExceptValue: deps.sumSubMarketIds,
              showInFilterComponent: false,
            }),
          ]
        : []),
      ...(shell.onion ? [new DashboardWzClassSingleSelectFilter(deps.localDatasetsService, {})] : []),
      ...createKpiFilters(axes.metric, axes.granularity.id),
      new DashboardMethodologyFromRouteSingleSelectFilter(deps.localDatasetsService, deps.activatedRoute),
      ...(shell.onion ? [new DashboardWzLayerVisibilitySingleSelectFilter(deps.localDatasetsService, {})] : []),
    ],

    preFilterTransformers: (_deps) => [
      new UndefinedStateToSumGermanyDashboardDataTransformer(),
      ...(shell.onion ? [new SourceIdToEntityIdDashboardDataTransformer()] : []),
    ],

    postFilterTransformers: (deps) => [
      ...valueColumns.map(
        ({ measure, resultColumn, fromMetric }) =>
          new KpiColumnDataTransformer({ measure, resultColumn, context, fromMetric }),
      ),
      ...(metric.extraRanks ?? []).map(
        ({ rankColumn, valueColumn }) => new RankDashboardDataTransformer(rankColumn, valueColumn),
      ),
      ...(shell.rankByValue ? [new RankDashboardDataTransformer('rank', 'value')] : []),
      // Dossiers are state-scoped.
      ...createIdNameTransformers(deps, axes.granularity.id),
      new SumSubMarketNameDataTransformer(deps.localDatasetsService),
      ...(shell.noKpiLabel ? [] : [new KpiLabelDataTransformer()]),
      ...(shell.onion ? [new SankeyNodeNameMappingDashboardDataTransformer(deps.localDatasetsService)] : []),
    ],

    view: metric.view,
  });
}
